import { DealRequestAllocation } from '../../shared/models';
import { ReactComponent as Refinance } from '../../shared/icons/Refinance.svg';
import { useDebtScheduleQuery } from '../../shared/hooks';
import { Grid, Skeleton } from '@mui/material';
import { BooleanUtils, NumberUtils } from '../../shared/utils';
import { DebtCapitalHeading } from './DebtCapitalHeading';
import {
	CerebroComplexListItem,
	CerebroHeading,
	CerebroLabel,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';

interface DebtRefinanceProps {
	allocation: DealRequestAllocation;
}

export const DebtRefinance: React.FC<DebtRefinanceProps> = ({ allocation }) => {
	const isEmpty = isRefinanceEmpty(allocation);
	const debtScheduleQuery = useDebtScheduleQuery(allocation.dealGuid);

	if (debtScheduleQuery.isLoading) {
		return <Skeleton height={400} />;
	}

	const amountRequested = NumberUtils.Sum(
		...(debtScheduleQuery.data?.filter(item => item.isRefinance == true).map(item => item.presentBalance ?? 0) ??
			[])
	);

	return (
		<>
			<DebtCapitalHeading icon={Refinance} amountRequested={amountRequested}>
				Refinance
			</DebtCapitalHeading>

			{isEmpty ? (
				<>
					<CerebroText variant="hint" gutterBottom>
						Details will be added in the Existing Debt Page
					</CerebroText>

					<CerebroStyledBox sx={{ marginBottom: 8 }}>
						<CerebroHeading size="h2" component="h2">
							No details have been added yet.
						</CerebroHeading>
						<br />
					</CerebroStyledBox>
				</>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }}>
					<Grid container>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								The loan(s) will be maturing soon
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceMaturingSoon)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Looking for lower rate on my debt
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceLowerRate)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Extend amortization to improve free cash flow
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceExtendAmortization)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Refinance and increase the size of the loan
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceIncreaseLoanSize)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								I am looking to exit the relationship with the current lender
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceNewLender)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>Other</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.refinanceOther)}
							</CerebroText>
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}
		</>
	);
};

export function isRefinanceEmpty(allocation: DealRequestAllocation): boolean {
	return (
		!allocation.refinanceMaturingSoon &&
		!allocation.refinanceLowerRate &&
		!allocation.refinanceExtendAmortization &&
		!allocation.refinanceIncreaseLoanSize &&
		!allocation.refinanceNewLender &&
		!allocation.refinanceOther
	);
}
